import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LogIn, Building2, UserPlus, ShieldCheck } from "lucide-react";
import { ORG } from "@/data/tools";
import { api, errorText, PASSWORD_HINT } from "@/lib/api";
import { TextField, Pills } from "@/components/tools/fields";
import { useWorkspace } from "@/context/WorkspaceContext";

const useSubmit = (url, toBody) => {
  const { signedIn } = useWorkspace();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e, extra) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(url, toBody(extra));
      await signedIn(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return { submit, error, busy };
};

const Err = ({ error, testid }) => (error ? <p role="alert" className="font-bold" style={{ color: "var(--rag-red-fg)" }} data-testid={testid}>{error}</p> : null);

const SignIn = () => {
  const [f, setF] = useState({ email: "", password: "", access_code: "", otp: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const { submit, error, busy } = useSubmit("/rc/login", () => f);
  const mfa = error === "MFA_REQUIRED" || f.otp;
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="ws-signin-form">
      <TextField id="si-email" label="School email" type="email" autoComplete="username" value={f.email} onChange={set("email")} testid="ws-signin-email" />
      <TextField id="si-password" label="Password" type="password" autoComplete="current-password" value={f.password} onChange={set("password")} testid="ws-signin-password" />
      <TextField id="si-code" label="Organisation access code (staff and governors)" value={f.access_code} onChange={(v) => set("access_code")(v.toUpperCase())} testid="ws-signin-code" placeholder="e.g. GREENWOOD2027-4F2A" />
      <p className="text-sm text-ink-soft">SENDCOs and Headteachers who manage the workspace can leave the access code blank.</p>
      {mfa && <TextField id="si-otp" label="6-digit code from your authenticator app" inputMode="numeric" autoComplete="one-time-code" value={f.otp} onChange={set("otp")} testid="ws-signin-otp" />}
      <Err error={error === "MFA_REQUIRED" ? "" : error} testid="ws-signin-error" />
      {error === "MFA_REQUIRED" && <p className="flex items-center gap-2 font-bold" role="status"><ShieldCheck size={16} aria-hidden /> Two-step verification is on. Enter your code to continue.</p>}
      <button type="submit" disabled={busy} className="btn btn-brand w-full" data-testid="ws-signin-submit"><LogIn size={16} aria-hidden /> {busy ? "Signing in…" : "Sign in"}</button>
      <details className="text-sm text-ink-soft" data-testid="ws-forgot">
        <summary className="cursor-pointer font-bold text-ink">Forgotten your password?</summary>
        <p className="mt-2">For security, passwords can't be recovered, only reset. Staff and governors: ask your SENDCO or Headteacher for a reset link (it works for 15 minutes). SENDCOs and Headteachers: email <a href={`mailto:${ORG.datalead}?subject=${encodeURIComponent("Workspace password reset request")}`} className="break-all font-bold underline" data-testid="auth-datalead-email">{ORG.datalead}</a> from your school email address.</p>
      </details>
    </form>
  );
};

const Account = ({ f, set, prefix }) => (
  <>
    <TextField id={`${prefix}-name`} label="Your name" autoComplete="name" value={f.name} onChange={set("name")} testid={`${prefix}-name`} />
    <TextField id={`${prefix}-email`} label="School email" type="email" autoComplete="email" value={f.email} onChange={set("email")} testid={`${prefix}-email`} />
    <TextField id={`${prefix}-password`} label="Create a password" type="password" autoComplete="new-password" value={f.password} onChange={set("password")} testid={`${prefix}-password`} aria-describedby={`${prefix}-pw-hint`} />
    <p id={`${prefix}-pw-hint`} className="-mt-2 text-sm text-ink-soft">{PASSWORD_HINT}</p>
    <div className="hidden" aria-hidden="true"><label htmlFor={`${prefix}-website`}>Website</label><input id={`${prefix}-website`} tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)} /></div>
  </>
);

const CreateSchool = () => {
  const [f, setF] = useState({ school_name: "", name: "", email: "", password: "", role: "sendco", website: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const { submit, error, busy } = useSubmit("/rc/register", () => f);
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="ws-create-form">
      <TextField id="cs-school" label="School or organisation name" value={f.school_name} onChange={set("school_name")} testid="ws-create-school" />
      <Pills legend="Your role" hideLegend={false} name="cs-role" value={f.role} onChange={set("role")} testid="ws-create-role"
        options={[{ v: "sendco", label: "SENDCO" }, { v: "head", label: "Headteacher" }]} />
      <Account f={f} set={set} prefix="ws-create" />
      <p className="text-sm text-ink-soft">You'll manage the workspace and get an access code to share with staff and governors.</p>
      <Err error={error} testid="ws-create-error" />
      <button type="submit" disabled={busy} className="btn btn-brand w-full" data-testid="ws-create-submit"><Building2 size={16} aria-hidden /> {busy ? "Creating…" : "Create school workspace"}</button>
    </form>
  );
};

const JoinSchool = () => {
  const [f, setF] = useState({ access_code: "", name: "", email: "", password: "", role: "staff", website: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const { submit, error, busy } = useSubmit("/rc/join", () => f);
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="ws-join-form">
      <TextField id="js-code" label="Organisation access code" value={f.access_code} onChange={(v) => set("access_code")(v.toUpperCase())} testid="ws-join-code" placeholder="From your SENDCO or Headteacher" />
      <Pills legend="Your role" hideLegend={false} name="js-role" value={f.role} onChange={set("role")} testid="ws-join-role"
        options={[{ v: "staff", label: "Staff member" }, { v: "governor", label: "Governor (view only)" }]} />
      <Account f={f} set={set} prefix="ws-join" />
      <Err error={error} testid="ws-join-error" />
      <button type="submit" disabled={busy} className="btn btn-brand w-full" data-testid="ws-join-submit"><UserPlus size={16} aria-hidden /> {busy ? "Joining…" : "Join workspace"}</button>
    </form>
  );
};

const MODES = [
  { id: "signin", label: "Sign in", El: SignIn },
  { id: "create", label: "Create school", El: CreateSchool },
  { id: "join", label: "Join school", El: JoinSchool },
];

export const AuthPanel = () => {
  const [params, setParams] = useSearchParams();
  const mode = MODES.find((m) => m.id === params.get("mode")) || MODES[0];
  return (
    <div className="mx-auto max-w-lg rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-8" data-testid="ws-auth-panel">
      <div role="tablist" aria-label="Account options" className="mb-6 grid grid-cols-3 gap-1 rounded-full border-2 border-line p-1">
        {MODES.map((m) => (
          <button key={m.id} type="button" role="tab" aria-selected={m.id === mode.id} onClick={() => setParams({ mode: m.id })}
            className={`rounded-full px-2 py-2 text-sm font-bold transition-colors ${m.id === mode.id ? "bg-brand text-brand-fg" : "hover:bg-paper"}`} data-testid={`ws-tab-${m.id}`}>
            {m.label}
          </button>
        ))}
      </div>
      <mode.El key={mode.id} />
    </div>
  );
};
